import { create } from "zustand";
import {
  getOrCreateCart,
  addToCart as apiAddToCart,
  updateCartItem as apiUpdateCartItem,
  removeCartItem as apiRemoveCartItem,
  mergeGuestCart as apiMergeGuestCart,
  cartTotals,
} from "@/lib/commerce/api";
// Cart-line hydration (read) goes through the adapter seam; mutations stay in api.ts.
import { getCartLines } from "@/lib/commerce/adapter";
import type { CommerceCartLine } from "@/lib/commerce/types";

interface CommerceCartState {
  cartId: string | null;
  lines: CommerceCartLine[];
  isLoading: boolean;
  count: number;
  subtotal: number;
  init: () => Promise<void>;
  refresh: () => Promise<void>;
  mergeOnSignIn: () => Promise<void>;
  add: (opts: { productId: string; variantId: string; unitPrice: number; quantity?: number }) => Promise<void>;
  setQuantity: (itemId: string, quantity: number) => Promise<void>;
  remove: (itemId: string) => Promise<void>;
}

export const useCommerceCart = create<CommerceCartState>((set, get) => ({
  cartId: null,
  lines: [],
  isLoading: false,
  count: 0,
  subtotal: 0,

  init: async () => {
    if (get().cartId) {
      await get().refresh();
      return;
    }
    set({ isLoading: true });
    try {
      const cartId = await getOrCreateCart();
      const lines = await getCartLines(cartId);
      const { count, subtotal } = cartTotals(lines);
      set({ cartId, lines, count, subtotal });
    } catch (e) {
      console.error("Commerce cart init failed:", e);
    } finally {
      set({ isLoading: false });
    }
  },

  refresh: async () => {
    const cartId = get().cartId;
    if (!cartId) return;
    const lines = await getCartLines(cartId);
    const { count, subtotal } = cartTotals(lines);
    set({ lines, count, subtotal });
  },

  mergeOnSignIn: async () => {
    try {
      const mergedCartId = await apiMergeGuestCart();
      const cartId = mergedCartId ?? (await getOrCreateCart());
      const lines = await getCartLines(cartId);
      const { count, subtotal } = cartTotals(lines);
      set({ cartId, lines, count, subtotal });
    } catch (e) {
      console.error("Commerce cart merge failed:", e);
    }
  },


  add: async ({ productId, variantId, unitPrice, quantity }) => {
    let cartId = get().cartId;
    if (!cartId) {
      cartId = await getOrCreateCart();
      set({ cartId });
    }
    await apiAddToCart({ cartId, productId, variantId, unitPrice, quantity });
    await get().refresh();
  },

  setQuantity: async (itemId, quantity) => {
    const cartId = get().cartId;
    if (!cartId) return;
    await apiUpdateCartItem(cartId, itemId, quantity);
    await get().refresh();
  },

  remove: async (itemId) => {
    const cartId = get().cartId;
    if (!cartId) return;
    await apiRemoveCartItem(cartId, itemId);
    await get().refresh();
  },

}));
